import { ServerPageData } from '@/features/product-editor/types';
import { SectionType, StoreTheme } from '@/types/store';
import { isEqual } from 'lodash';
import { CustomizationStoreState } from '../stores/customizationStore';
import {
    EditorialTextItem,
    GalleryItem,
    ItemType,
    PageItem,
    PageOrderItem,
    SectionItem,
    SectionOrderItem,
    StoreData,
    StoreItem,
    TestimonialsItem,
    TitleItem,
} from '../types/store';

//************************************************************************************
// Change tracking interfaces
//************************************************************************************
export interface SectionChanges {
    reordered: number[];
    added: number[];
    removed: number[];
    updated: number[];
}

export interface PageChanges {
    reordered: number[];
    updated: number[];
}

export interface StoreChanges {
    sections: SectionChanges;
    pages: PageChanges;
}

/** Type guard for section items - narrows to SectionItem union */
export const isSection = (item: StoreItem): item is SectionItem => {
    return (
        item.type === SectionType.TITLE ||
        item.type === SectionType.EDITORIAL_TEXT ||
        item.type === SectionType.GALLERY ||
        item.type === SectionType.TESTIMONIALS
    );
};

/** Type guard for page items */
export const isPage = (item: StoreItem): item is PageItem => {
    return item.type === ItemType.PAGE;
};

/** Type guard for editorial text items */
export const isEditorialText = (item: StoreItem): item is EditorialTextItem => {
    return item.type === SectionType.EDITORIAL_TEXT;
};

/** Type guard for gallery items */
export const isGallery = (item: StoreItem): item is GalleryItem => {
    return item.type === SectionType.GALLERY;
};

/** Type guard for testimonials items */
export const isTestimonials = (item: StoreItem): item is TestimonialsItem => {
    return item.type === SectionType.TESTIMONIALS;
};

/** Type guard for title items */
export const isTitle = (item: StoreItem): item is TitleItem => {
    return item.type === SectionType.TITLE;
};

//************************************************************************************
// Store change detection
//************************************************************************************

/**
 * Compares current state with cached state to identify what has changed.
 * Returns IDs of sections and pages that need to be synced to the backend.
 */
export const identifyStoreChanges = (
    currentState: CustomizationStoreState,
    cachedState: Partial<CustomizationStoreState> | null
): StoreChanges => {
    if (!cachedState || !cachedState.storeItems) {
        // If no cached state, everything in current state is new
        const pages = currentState.storeItems.filter(isPage);
        const sections = currentState.storeItems.filter(isSection);

        return {
            sections: {
                reordered: [],
                added: sections.map(s => Number(s.id)).filter(id => !isNaN(id)),
                removed: [],
                updated: [],
            },
            pages: {
                reordered: [],
                updated: pages.map(p => Number(p.id)).filter(id => !isNaN(id)),
            },
        };
    }

    const currentItems = currentState.storeItems;
    const cachedItems = cachedState.storeItems || [];

    // Create maps for easier lookup
    const currentItemsMap = new Map<string | number, StoreItem>();
    const cachedItemsMap = new Map<string | number, StoreItem>();

    currentItems.forEach(item => {
        currentItemsMap.set(item.id, item);
    });

    cachedItems.forEach(item => {
        cachedItemsMap.set(item.id, item);
    });

    // Identify section changes
    const sectionChanges: SectionChanges = {
        reordered: [],
        added: [],
        removed: [],
        updated: [],
    };

    // Identify page changes
    const pageChanges: PageChanges = {
        reordered: [],
        updated: [],
    };

    // Check current sections
    currentItems.filter(isSection).forEach(currentSection => {
        // Check if it's a string ID (new section) or number ID (existing section)
        const isStringId = typeof currentSection.id === 'string';

        if (isStringId) {
            // String ID means it's a new section that hasn't been saved yet
            // We can't add it to the added array since we need a numeric ID
            // These will be handled separately when creating new sections
            return;
        }

        const sectionId = Number(currentSection.id);
        if (isNaN(sectionId)) return;

        const cachedSection = cachedItemsMap.get(currentSection.id);

        if (!cachedSection) {
            // Section not in cached state = newly added (but has numeric ID, so it was created)
            sectionChanges.added.push(sectionId);
        } else if (isSection(cachedSection)) {
            // Section exists in both, check for changes
            const cachedOrder = cachedSection.order;
            const currentOrder = currentSection.order;

            // Check if order changed
            if (cachedOrder !== currentOrder) {
                if (!sectionChanges.reordered.includes(sectionId)) {
                    sectionChanges.reordered.push(sectionId);
                }
            }

            // Check if section data changed (deep comparison of entire data object)
            // Both are now properly typed as SectionItem, so data is correctly typed
            const dataChanged = !isEqual(currentSection.data, cachedSection.data);

            if (dataChanged && !sectionChanges.updated.includes(sectionId)) {
                sectionChanges.updated.push(sectionId);
            }
        }
    });

    // Check for removed sections (in cached but not in current)
    cachedItems.filter(isSection).forEach(cachedSection => {
        const sectionId = Number(cachedSection.id);
        if (isNaN(sectionId)) return;

        if (!currentItemsMap.has(cachedSection.id)) {
            // Section was removed
            sectionChanges.removed.push(sectionId);
        }
    });

    // Check current pages
    currentItems.filter(isPage).forEach(currentPage => {
        // Check if it's a string ID (new page) or number ID (existing page)
        const isStringId = typeof currentPage.id === 'string';

        if (isStringId) {
            // String ID means it's a new page that hasn't been saved yet
            // These will be handled separately
            return;
        }

        const pageId = Number(currentPage.id);
        if (isNaN(pageId)) return;

        const cachedPage = cachedItemsMap.get(currentPage.id);

        if (!cachedPage) {
            // Page not in cached state = newly added or needs update
            pageChanges.updated.push(pageId);
        } else if (isPage(cachedPage)) {
            // Page exists in both, check for changes
            const cachedOrder = cachedPage.order;
            const currentOrder = currentPage.order;

            // Check if order changed
            if (cachedOrder !== currentOrder) {
                if (!pageChanges.reordered.includes(pageId)) {
                    pageChanges.reordered.push(pageId);
                }
            }

            // Check if page button data changed
            // Both are now properly typed as PageItem, so data is correctly typed
            const dataChanged = !isEqual(currentPage.data, cachedPage.data);

            if (dataChanged && !pageChanges.updated.includes(pageId)) {
                pageChanges.updated.push(pageId);
            }
        }
    });

    // Check for removed pages (in cached but not in current)
    cachedItems.filter(isPage).forEach(cachedPage => {
        const pageId = Number(cachedPage.id);
        if (isNaN(pageId)) return;

        if (!currentItemsMap.has(cachedPage.id)) {
            // Page was removed - this might not happen often, but handle it
            // Note: Pages are usually not deleted through this flow, but we track it
        }
    });

    return {
        sections: sectionChanges,
        pages: pageChanges,
    };
};

//************************************************************************************
// Order item helpers
//************************************************************************************

/**
 * Gets section order items for API calls.
 * Returns all sections with their current order (needed when any section is reordered).
 */
export const getSectionOrderItems = (currentState: CustomizationStoreState): SectionOrderItem[] => {
    const currentSections = currentState.storeItems
        .filter((item): item is SectionItem => isSection(item) && typeof item.id === 'number')
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return currentSections.map(section => ({
        section_id: Number(section.id),
        order: section.order ?? 0,
    }));
};

/**
 * Gets page order items for API calls.
 * Returns all pages with their current order (needed when any page is reordered).
 */
export const getPageOrderItems = (currentState: CustomizationStoreState): PageOrderItem[] => {
    const currentPages = currentState.storeItems
        .filter((item): item is PageItem => isPage(item) && typeof item.id === 'number')
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return currentPages.map(page => ({
        page_id: Number(page.id),
        order: page.order ?? 0,
    }));
};

//************************************************************************************
// Item getters
//************************************************************************************

/**
 * Gets section item by section ID.
 * Returns properly typed SectionItem or null.
 */
export const getSection = (currentState: CustomizationStoreState, sectionId: number): SectionItem | null => {
    const section = currentState.storeItems.find(item => isSection(item) && Number(item.id) === sectionId);
    return section && isSection(section) ? section : null;
};

/**
 * Gets page item by page ID.
 * Returns properly typed PageItem or null.
 */
export const getPage = (currentState: CustomizationStoreState, pageId: number): PageItem | null => {
    const pageBlock = currentState.storeItems.find(item => isPage(item) && Number(item.id) === pageId);
    return pageBlock && isPage(pageBlock) ? pageBlock : null;
};

/**
 * Gets page button data for a given page ID.
 * Returns the properly typed page data (button config) or null.
 */
export const getPageButton = (
    currentState: CustomizationStoreState,
    pageId: number
): ServerPageData['button'] | null => {
    const pageBlock = getPage(currentState, pageId);
    return (pageBlock?.data?.data as ServerPageData | undefined)?.button ?? null;
};

/**
 * Builds storeData object from current state for API calls.
 */
export const buildStoreData = (state: CustomizationStoreState): StoreData => {
    const storeData: StoreData = { theme: state.theme || StoreTheme.BRIM };
    if (state.primaryFont) storeData.primary_font = state.primaryFont;
    if (state.secondaryFont) storeData.secondary_font = state.secondaryFont;
    if (state.imageFrames) storeData.image_frame = state.imageFrames;
    if (state.header) storeData.header = state.header;
    if (state.colors) storeData.colors = { default: state.colors };
    return storeData;
};

/**
 * Builds storeData object from cachedState for comparison.
 */
const buildStoreDataFromCachedState = (cachedState: Partial<CustomizationStoreState>): StoreData => {
    return buildStoreData(cachedState as CustomizationStoreState);
};

/**
 * Checks if storeData has changed compared to cachedState.
 * Returns true if there are changes, false otherwise.
 */
export const hasStoreDataChanges = (
    currentState: CustomizationStoreState,
    cachedState: Partial<CustomizationStoreState> | null
): boolean => {
    if (!cachedState) {
        return true; // No cached state means we need to update
    }

    const currentStoreData = buildStoreData(currentState);
    const cachedStoreData = buildStoreDataFromCachedState(cachedState);

    return !isEqual(currentStoreData, cachedStoreData);
};
